import React from 'react';
import { useSelector } from 'react-redux';
import { getAccountCurrency } from 'redux/selectors/accounts';
import type { Currency } from 'types/account';
import { formatCurrency } from 'utils/currency';
import AnimatedNumber from './AnimatedNumber';

export interface Props {
  currency?: Currency;
  value: number | null;
  minimumFractionDigits?: number;
  className?: string;
  animated?: boolean;
  testId?: string;
  zeroPlaceholder?: React.ReactNode;
  style?: React.CSSProperties;
}

const CurrencyFormat: React.FC<Props> = ({
  animated = true,
  currency,
  value = 0,
  minimumFractionDigits = 0,
  className,
  testId,
  zeroPlaceholder,
  style,
}) => {
  const accountCurrency = useSelector(getAccountCurrency);

  if (value === 0 && zeroPlaceholder) {
    return React.isValidElement(zeroPlaceholder) ? zeroPlaceholder : <>{zeroPlaceholder}</>;
  }

  const formatValue = (value: number | null, digits: number) =>
    formatCurrency(currency ?? accountCurrency, value, digits);

  return (
    <span className={className} data-testid={testId || 'currency-format'} style={style}>
      <span data-testid="amount">
        {animated ? (
          <AnimatedNumber
            value={value ?? 0}
            format={val => formatValue(val, minimumFractionDigits)}
          />
        ) : (
          formatValue(value, minimumFractionDigits)
        )}
      </span>
    </span>
  );
};

export default CurrencyFormat;

/**
 * Shows 0 formatted with the current currency, intended as a placeholder or default value.
 */
export const CurrencyFormatZero = React.memo(() => {
  const currency = useSelector(getAccountCurrency);
  return (
    <CurrencyFormat animated={false} value={0} currency={currency} minimumFractionDigits={2} />
  );
});
